import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Col, Row } from "react-bootstrap"
import { Spin, message, Anchor, Button, Dropdown, Space, Popconfirm, Radio as AntRadio } from "antd"
import { CloseOutlined, DownOutlined, PlusOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import { partyDebouncedSave } from "./partyDebouncedSave"
import { companyAddressForBroker, mergeBrokerSeed } from "./brokerSeed"
import {
    getLosApplicationV2,
    addLosCoborrowerV2,
    deleteLosCoborrowerV2,
    patchParticipantAssetsV2,
    patchParticipantDeclarationsV2,
    patchOccupationCurrentV2,
    patchParticipantEmploymentV2,
    patchParticipantLiabilitiesV2,
    patchParticipantOtherIncomeV2,
    patchParticipantProfileV2,
    patchParticipantRealEstateV2,
    patchParticipantResidenceV2,
    patchParticipantSsnV2,
    patchPropertyFieldsV2,
    patchPropertyV2,
    patchSelectionV2,
    patchLoanFieldsV2,
    patchBrokerInfoV2,
    patchIncomeFromCalculationV2,
} from "../DocumentChecklist/applicationV2Client"
import { track } from 'statsig'
import { STATSIG } from 'statsig/events'
import IncomeCalculationModalV2 from "./IncomeCalculationModalV2"
import BusinessIncomeCalculationModalV2 from "./BusinessIncomeCalculationModalV2"
import { adaptV2ApplicationForIncomeCalc, personDisplayNameV2 } from "./incomeCalculationV2View"
import { participantCompletionPercent } from "./completedStepsProgress"
import Interview1003ViewV2More, {
    buildV2AnchorItems,
    FormChromeProvider,
    Field,
    TextField,
    PrefixField,
    DurationFields,
    SelectField,
    DateField,
    SelectionField,
    YesNoField,
    ChromeCheckbox,
    ChromeRadioGroup,
    StateSelect,
    CountySelect,
    StreetAddressField,
    StructuredAddressField,
    isLenderLoanInformationEnabled,
    assetsHaveIncompleteJointOwnership,
    liabilitiesHaveIncompleteJointOwnership,
    displayMonthlyIncome,
    sumMonthlyIncomeFields,
    displayMonthlyIncomeOrLoss,
    moneyTypingValue,
    MoneyPrefixField,
    MoneyTextField,
    hasNonOwnSharedOwnerConflict,
    isNonOwnSharedOwnerWarningDismissed,
    dismissNonOwnSharedOwnerWarning,
    nonOwnSharedOwnerWarningText,
    NonOwnSharedOwnerWarningBanner,
} from "./Interview1003ViewV2More"
import LenderLoanFormV2 from "./LenderLoanFormV2"
import { fetchCreditReports } from "../CreditReportV2/creditReportV2Utils"
import * as com from "../Common.js"
import { checkAccess } from "../Auth"
import { LS_Disposed, LS_LoanFunded, UR_Owner, UR_LoanOfficerManager } from "../State.js"
import "./Interview1003ViewV2.css"

const suffixOptions = [
    { value: "", label: "-- No Suffix --" },
    { value: "Jr.", label: "Jr." },
    { value: "Sr.", label: "Sr." },
    { value: "I", label: "I" },
    { value: "II", label: "II" },
    { value: "III", label: "III" },
    { value: "IV", label: "IV" },
    { value: "Ph.D.", label: "Ph.D." },
    { value: "M.D.", label: "M.D." },
    { value: "Esq.", label: "Esq." },
    { value: "J.D.", label: "J.D." },
    { value: "CPA", label: "CPA" },
    { value: "DDS", label: "DDS" },
    { value: "KBE", label: "KBE" },
    { value: "OBE", label: "OBE" },
    { value: "MBE", label: "MBE" },
]

const emptyAddress = {
    street: "",
    unit: "",
    city: "",
    county: "",
    state: "",
    zip: "",
    years: "",
    months: "",
}

const hasFilledEmploymentRow = (occ) => {
    if (!occ || typeof occ !== "object") return false
    const t = (v) => typeof v === "string" && v.trim() !== ""
    if (t(occ.employername) || t(occ.positiontitle) || t(occ.from)) return true
    if (occ.selfemployed && t(occ.incomeorloss)) return true
    const inc = occ.income || {}
    return ["base", "overtime", "bonuses", "commissions", "military", "other"].some((k) => t(inc[k]))
}

/** V2 section 1b has no employed/unemployed radio; set flag when user fills employment. */
const withEmployedHasOccupation = (occupation, otheroccupations = []) => {
    const occ = { ...occupation }
    if (occ.hasoccupation === "retired") return occ
    const others = Array.isArray(otheroccupations) ? otheroccupations : []
    if (hasFilledEmploymentRow(occ) || others.some(hasFilledEmploymentRow)) {
        occ.hasoccupation = "employed"
    }
    return occ
}

const emptyOccupation = {
    hasoccupation: "",
    employername: "",
    employeraddress: "",
    employerstreet: "",
    employerunit: "",
    employercity: "",
    employercounty: "",
    employerstate: "",
    employerzip: "",
    from: "",
    positiontitle: "",
    typeofbusiness: "",
    phone: "",
    yearsatlineofwork: "",
    monthsatlineofwork: "",
    selfemployed: false,
    employedbyagent: false,
    percentownership: "",
    incomeorloss: "",
    incomeorlossperiod: "monthly",
    income: {
        base: "",
        basemonthly: "monthly",
        overtime: "",
        overtimemonthly: "monthly",
        bonuses: "",
        bonusesmonthly: "monthly",
        commissions: "",
        commissionsmonthly: "monthly",
        military: "",
        militarymonthly: "monthly",
        other: "",
        othermonthly: "monthly",
    },
}

const getWorkDuration = (emp) => {
    const years = emp?.yearsatlineofwork
    const months = emp?.monthsatlineofwork
    if (years !== undefined && years !== "" && years !== null) {
        return { years, months: months || "" }
    }
    if (months !== undefined && months !== "" && months !== null) {
        return { years: years || "", months }
    }
    if (emp?.from) {
        const start = dayjs(emp.from)
        if (start.isValid()) {
            const diffMonths = dayjs().diff(start, "month")
            return { years: String(Math.floor(diffMonths / 12)), months: String(diffMonths % 12) }
        }
    }
    return { years: years || "", months: months || "" }
}

const ownershipShareValue = (percentownership) => {
    if (percentownership === "less" || percentownership === "more") return percentownership
    return parseFloat(percentownership || 0) < 25 ? "less" : "more"
}

const canUseIncomeCalculation = (emp) => {
    if (!emp?.employername) return false
    if (!emp.selfemployed) return true
    const pct = emp.percentownership
    if (pct === "more") return true
    const n = parseFloat(pct)
    return !Number.isNaN(n) && n > 24
}

const formatSSN = (value) => {
    const digits = `${value || ""}`.replace(/\D/g, "").slice(0, 9)
    if (digits.length <= 3) return digits
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
}

/** UI-only format hint for 1a Contact Email; blank is handled by required warning. */
const emailFormatError = (email) => {
    const value = `${email || ""}`.trim()
    if (!value) return undefined
    return com.emailRegex.test(value) ? undefined : "Please enter a valid email address"
}

// Keys that updateLenderLoanFormFromState actually copies from 1003 → L1–L4.
const lenderFormRefreshKeys = {
    purpose: true,
    salesprice: true,
    appraisal: true,
    loansize: true,
    cleanenergylien: true,
    firstname: true,
    lastname: true,
}

const shouldRefreshLenderForm = (keys) => {
    const list = Array.isArray(keys) ? keys : [keys]
    return list.some((key) => lenderFormRefreshKeys[`${key || ""}`.toLowerCase()])
}

// Closer to legacy local save throttle (1s). Server updatestate was 5s; V2 modular PATCH uses 1s debounce.
const SAVE_DEBOUNCE_MS = 1000

const V2_ANCHOR_IDS = [
    "s1", "1a", "1b", "1c", "1d", "1e",
    "s2", "2a", "2b", "2c", "2d",
    "s3", "3a", "3b", "3c",
    "s4", "4a", "4b", "4c", "4d",
    "s5", "5a", "5b",
    "s6", "s7", "s8", "s9",
    "LenderLoanForm", "L1", "L2", "L3", "L4",
]

const updateAnchorActiveState = (activeKey) => {
    document.querySelectorAll(".ant-anchor-link").forEach((link) => {
        link.classList.remove("ant-anchor-link-active")
    })
    const activeAnchor = document.querySelector(`[href="#${activeKey}"]`)
    const anchorLink = activeAnchor?.closest(".ant-anchor-link")
    if (anchorLink) anchorLink.classList.add("ant-anchor-link-active")
}

const scrollToAnchor = (href) => {
    const escapeSelector = (selector) => selector.replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, "\\$1")
    const retryScroll = (attempts) => {
        const target = document.querySelector(escapeSelector(href))
        if (target) {
            target.scrollIntoView({ behavior: "smooth" })
        } else if (attempts > 0) {
            setTimeout(() => retryScroll(attempts - 1), 100)
        } else {
            window.location.hash = href
        }
    }
    retryScroll(5)
}

const participantDisplayName = (person) => {
    const name = [person?.firstName, person?.middleName, person?.lastName]
        .map((part) => `${part || ""}`.trim())
        .filter(Boolean)
        .join(" ")
    return name || "Applicant"
}

const resolvePartyIdFromView = (application, view) => {
    if (!application) return ""
    if (view === "coborrower") {
        const order = application.participantOrder || Object.keys(application.participantsById || {})
        const coId = order.find((id) => id && id !== application.primaryPartyId)
        return coId || application.primaryPartyId || ""
    }
    return application.primaryPartyId || ""
}

/** Pick which participant tab should be active. During async reload, prefer the current ref so a in-flight load does not snap back to the first co-borrower. */
const pickActivePartyId = (application, {
    currentPartyId = "",
    partyId = "",
    partyName = "",
    view = "",
    respectView = false,
} = {}) => {
    if (!application) return ""
    const byId = application.participantsById || {}
    const primaryId = application.primaryPartyId || ""

    if (currentPartyId && byId[currentPartyId]) {
        if (!respectView) return currentPartyId
        if (view === "borrower" && currentPartyId === primaryId) return currentPartyId
        if (view === "coborrower" && currentPartyId !== primaryId) return currentPartyId
        if (view !== "borrower" && view !== "coborrower") return currentPartyId
    }

    if (partyId && byId[partyId]) return partyId

    if (view === "borrower") return primaryId
    if (view === "coborrower") return resolvePartyIdFromView(application, "coborrower")

    if (partyName) {
        const match = Object.values(byId).find((p) => {
            const name = [p.firstName, p.lastName].filter(Boolean).join(" ")
            return name === partyName
        })
        return match?.partyId || primaryId
    }

    return primaryId
}

const listBorrowerParticipants = (application) => {
    if (!application?.participantsById) return []
    const order = application.participantOrder?.length
        ? application.participantOrder
        : Object.keys(application.participantsById)
    return order
        .map((id) => application.participantsById[id])
        .filter((person) => {
            if (!person?.partyId) return false
            const role = `${person.role || ""}`.toLowerCase()
            return person.isBorrower || person.isPrimary || role === "borrower" || role === "coborrower"
        })
}

const participantInitial = (person) => {
    const name = participantDisplayName(person)
    const ch = (name || "?").trim().charAt(0)
    return ch ? ch.toUpperCase() : "?"
}

const participantRoleLabel = (person, primaryPartyId) => {
    if (person?.isPrimary || person?.partyId === primaryPartyId) return "Borrower"
    return "Co-borrower"
}

// Identity / completion flags come from the latest modular PATCH response.
const SERVER_PARTICIPANT_META_KEYS = [
    "partyId",
    "userId",
    "role",
    "position",
    "isPrimary",
    "isBorrower",
    "useToQualify",
    "completedSteps",
    "estimatedFico",
]

const SERVER_APP_META_KEYS = [
    "loanId",
    "primaryPartyId",
    "participantOrder",
    "completedStepsByPartyId",
    "currentStage",
    "currentStatus",
    "updatedAt",
    "companyName",
    "creditWarnings",
    "creditconsented",
    "creditconsentdate",
]

const mergeFinanceMapsFromServer = (localFinance, serverFinance, keys, activePartyId, replaceActiveKeys = []) => {
    if (!serverFinance || !keys?.length) return localFinance
    const nextFinance = { ...(localFinance || {}) }
    const replaceActive = new Set(replaceActiveKeys || [])
    keys.forEach((key) => {
        const serverMap = serverFinance[key]
        if (!serverMap || typeof serverMap !== "object") return
        const localMap = nextFinance[key] || {}
        const nextMap = { ...localMap }
        Object.keys(serverMap).forEach((partyId) => {
            // Keep the active party's in-progress edits unless this key must take
            // the server snapshot (e.g. residence sync creates/deletes current-residence REO).
            if (partyId === activePartyId && !replaceActive.has(key)) return
            nextMap[partyId] = serverMap[partyId]
        })
        nextFinance[key] = nextMap
    })
    return nextFinance
}

// Modular PATCH returns a full Application snapshot that can lag behind newer
// keystrokes. Keep the current local editable tree and only merge server-owned
// metadata (and optional explicit server field overrides).
const mergeApplicationFromSave = (serverApp, localApp, options = {}) => {
    if (!serverApp) return localApp
    if (!localApp) return serverApp

    const serverById = serverApp.participantsById || {}
    const localById = localApp.participantsById || {}
    const participantsById = {}

    Object.keys(serverById).forEach((id) => {
        const serverP = serverById[id]
        const localP = localById[id]
        if (!localP) {
            participantsById[id] = serverP
            return
        }
        const next = { ...localP }
        SERVER_PARTICIPANT_META_KEYS.forEach((key) => {
            if (serverP[key] !== undefined) next[key] = serverP[key]
        })
        if (options.serverOccupationPartyId === id) {
            next.occupation = serverP.occupation
            next.otheroccupations = serverP.otheroccupations
            next.previousoccupations = serverP.previousoccupations
        }
        participantsById[id] = next
    })

    const merged = { ...localApp, participantsById }
    SERVER_APP_META_KEYS.forEach((key) => {
        if (serverApp[key] !== undefined) merged[key] = serverApp[key]
    })
    if (options.mergeFinanceKeys?.length) {
        merged.finance = mergeFinanceMapsFromServer(
            localApp.finance,
            serverApp.finance,
            options.mergeFinanceKeys,
            options.activePartyId,
            options.replaceActiveFinanceKeys,
        )
    }
    return merged
}

export default function Interview1003ViewV2({
    loanId,
    partyId,
    partyName,
    onSwitchToPrimaryParty,
    // Loan page tab: party switcher + handoff + deep-link scroll
    showPartySwitcher = false,
    view,
    changeView,
    changeTab,
    targetHref,
    targetPartyId,
    onTargetPartyApplied,
    handingOff,
    handingOffType,
    borrowerId,
    onApplicationChange,
    syncApplication,
}) {
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState("")
    const [editMode, setEditMode] = useState(true)
    const [handingOffBusy, setHandingOffBusy] = useState(false)
    const [addingCoborrower, setAddingCoborrower] = useState(false)
    const [deletingCoborrowerPartyId, setDeletingCoborrowerPartyId] = useState("")
    const [application, setApplication] = useState(null)
    const [loanLimits, setLoanLimits] = useState({})
    const [activePartyId, setActivePartyId] = useState(partyId || "")
    const [lenderFormRefreshToken, setLenderFormRefreshToken] = useState(0)
    const [incomeCalc, setIncomeCalc] = useState(null)
    const [activeAnchorKey, setActiveAnchorKey] = useState("s1")
    const [selectedCreditReport, setSelectedCreditReport] = useState(null)
    const [creditLiability, setCreditLiability] = useState([])
    const participantRef = useRef(null)
    const applicationRef = useRef(null)
    const loanIdRef = useRef(loanId)
    const editModeRef = useRef(true)
    const setSavingRef = useRef(setSaving)
    const savingCountRef = useRef(0)
    const pendingSaveKeysRef = useRef(new Set())
    const bumpLenderFormRefreshRef = useRef(() => {})
    const profileNameDirtyRef = useRef(false)
    const pendingPropertyFieldsRef = useRef({})
    const pendingSelectionRef = useRef({})
    const pendingLoanBalanceRef = useRef(null)
    const flushPendingSavesRef = useRef(async () => {})
    const activeAnchorKeyRef = useRef("s1")
    const intersectionObserverRef = useRef(null)
    const scrolledHrefRef = useRef("")
    const appliedTargetPartyRef = useRef("")
    const activePartyIdRef = useRef(partyId || "")

    useEffect(() => {
        editModeRef.current = editMode
    }, [editMode])
    loanIdRef.current = loanId
    setSavingRef.current = setSaving

    const pendingSyncApplicationRef = useRef(null)

    // Track debounced + in-flight modular saves so parallel modules don't clear the
    // "Saving" spinner when the first request finishes.
    const syncSavingIndicator = () => {
        if (savingCountRef.current > 0 || pendingSaveKeysRef.current.size > 0) {
            setSavingRef.current("saving")
        } else {
            setSavingRef.current("")
        }
    }
    const applySyncApplicationNow = (incoming) => {
        if (!incoming || incoming === applicationRef.current) return
        applicationRef.current = incoming
        setApplication(incoming)
        const pid = activePartyIdRef.current || incoming.primaryPartyId
        if (pid && incoming.participantsById?.[pid]) {
            participantRef.current = incoming.participantsById[pid]
        }
    }
    const flushQueuedSyncApplication = () => {
        if (pendingSaveKeysRef.current.size > 0 || savingCountRef.current > 0) return
        const incoming = pendingSyncApplicationRef.current
        if (!incoming) return
        pendingSyncApplicationRef.current = null
        applySyncApplicationNow(incoming)
    }
    const markSavePending = (key) => {
        pendingSaveKeysRef.current.add(key)
        syncSavingIndicator()
    }
    const clearSavePending = (key) => {
        pendingSaveKeysRef.current.delete(key)
        syncSavingIndicator()
        flushQueuedSyncApplication()
    }
    const beginSave = (key) => {
        pendingSaveKeysRef.current.delete(key)
        savingCountRef.current += 1
        syncSavingIndicator()
    }
    const endSave = () => {
        savingCountRef.current = Math.max(0, savingCountRef.current - 1)
        syncSavingIndicator()
        flushQueuedSyncApplication()
    }
    // Debounced autosave for one 1003 section; fn receives the borrower the edit was made for
    // (see partyDebouncedSave), not whoever is active when the save fires.
    const trackDebouncedSave = (key, fn, errorFallback) => {
        const debounced = partyDebouncedSave(async (target, ...args) => {
            beginSave(key)
            try {
                await fn(target, ...args)
            } catch (err) {
                message.error(err?.message || errorFallback || "Failed to save")
            } finally {
                endSave()
            }
        }, SAVE_DEBOUNCE_MS, () => participantRef.current)
        const wrapped = (...args) => {
            // Schedule before marking: scheduling may first fire another borrower's pending save,
            // whose beginSave clears this key.
            const result = debounced(...args)
            markSavePending(key)
            return result
        }
        wrapped.cancel = () => {
            clearSavePending(key)
            debounced.cancel()
        }
        wrapped.flush = () => debounced.flush()
        return wrapped
    }

    const onApplicationChangeRef = useRef(onApplicationChange)
    onApplicationChangeRef.current = onApplicationChange
    useEffect(() => {
        if (!application) return
        const notify = onApplicationChangeRef.current
        if (notify) notify(application)
    }, [application])

    // Apply external V2 updates (e.g. Rate selection modal Update) without remounting.
    // Queue while local saves are pending so the update is not dropped forever.
    useEffect(() => {
        if (!syncApplication) return
        if (syncApplication === applicationRef.current) {
            pendingSyncApplicationRef.current = null
            return
        }
        if (pendingSaveKeysRef.current.size > 0 || savingCountRef.current > 0) {
            pendingSyncApplicationRef.current = syncApplication
            return
        }
        pendingSyncApplicationRef.current = null
        applySyncApplicationNow(syncApplication)
    }, [syncApplication])

    // Match legacy Interview1003View: re-read company flag each render.
    const showLenderLoanForm = isLenderLoanInformationEnabled()

    bumpLenderFormRefreshRef.current = () => {
        if (!isLenderLoanInformationEnabled()) return
        setLenderFormRefreshToken((n) => n + 1)
    }
    const bumpLenderFormRefresh = useCallback(() => {
        bumpLenderFormRefreshRef.current()
    }, [])

    const setActiveAnchor = useCallback((anchorId) => {
        if (!anchorId || activeAnchorKeyRef.current === anchorId) return
        activeAnchorKeyRef.current = anchorId
        setActiveAnchorKey(anchorId)
        updateAnchorActiveState(anchorId)
    }, [])

    const anchorItems = useMemo(
        () => buildV2AnchorItems(showLenderLoanForm, { compact: !showPartySwitcher }),
        [showLenderLoanForm, showPartySwitcher],
    )

    const participant = useMemo(() => {
        if (!application?.participantsById) return null
        const id = activePartyId || application.primaryPartyId
        return application.participantsById[id] || null
    }, [application, activePartyId])

    const [nonOwnSharedWarningTick, setNonOwnSharedWarningTick] = useState(0)
    const showNonOwnSharedOwnerWarning = useMemo(() => {
        void nonOwnSharedWarningTick
        if (!application || !participant) return false
        return hasNonOwnSharedOwnerConflict(application, participant)
            && !isNonOwnSharedOwnerWarningDismissed(loanId, participant.partyId)
    }, [application, participant, loanId, nonOwnSharedWarningTick])
    const onDismissNonOwnSharedOwnerWarning = useCallback(() => {
        dismissNonOwnSharedOwnerWarning(loanId, participant?.partyId)
        setNonOwnSharedWarningTick((n) => n + 1)
    }, [loanId, participant?.partyId])

    useEffect(() => {
        participantRef.current = participant
    }, [participant])
    useEffect(() => {
        applicationRef.current = application
    }, [application])
    useEffect(() => {
        activePartyIdRef.current = activePartyId
    }, [activePartyId])

    useEffect(() => {
        fetch("/data/loanlimits")
            .then((res) => {
                if (!res.ok) throw Error(res.statusText)
                return res.json()
            })
            .then((res) => setLoanLimits(res || {}))
            .catch((err) => console.error(err))
    }, [])

    const applyResult = (body, options = {}) => {
        if (!body?.Application) return
        const merged = options.replace
            ? body.Application
            : mergeApplicationFromSave(body.Application, applicationRef.current, options)
        applicationRef.current = merged
        const pid = activePartyIdRef.current
        if (pid && merged.participantsById?.[pid]) {
            participantRef.current = merged.participantsById[pid]
        }
        setApplication(merged)
    }
    const applyResultRef = useRef(applyResult)
    applyResultRef.current = applyResult

    const seedBrokerInfoFromApis = useCallback(async () => {
        if (!loanId) return
        const token = sessionStorage.getItem("ZeitroA")
        const headers = { Authorization: "Bearer " + token, Cache: "no-cache" }
        let seed = {}

        try {
            const companyRes = await fetch("/los/getcompanyprofilesetup", { method: "GET", headers, cache: "no-cache" })
            if (companyRes.ok) {
                const data = await companyRes.json()
                seed = {
                    ...seed,
                    name: data.Name || "",
                    ...companyAddressForBroker(data),
                    nmls: data.CompanyNMLS || "",
                    licenseid: data.LicenseId || "",
                }
            }
        } catch (err) {
            console.error(err)
        }

        try {
            const loRes = await fetch("/los/getloanprocessor", {
                method: "POST",
                headers,
                body: JSON.stringify({ LoanID: loanId }),
            })
            if (loRes.ok) {
                const js = await loRes.json()
                const lo = js.CurrentLO || {}
                seed = {
                    ...seed,
                    loanofficername: [lo.FirstName, lo.LastName].filter(Boolean).join(" "),
                    phone: lo.Phone || "",
                    email: lo.Email || "",
                    loanofficernmls: lo.NMLS || "",
                    loanofficerlicenseid: lo.LicenseID || "",
                }
            }
        } catch (err) {
            console.error(err)
        }

        const app = applicationRef.current
        if (!app) return
        const { next, changed } = mergeBrokerSeed(app.brokerInfo, seed)
        if (!changed) return
        const nextApp = { ...app, brokerInfo: next }
        applicationRef.current = nextApp
        setApplication(nextApp)
        try {
            applyResult(await patchBrokerInfoV2(loanId, next))
        } catch (err) {
            message.error(err.message || "Failed to save loan originator info")
        }
    }, [loanId])

    const load = useCallback(async () => {
        if (!loanId) return
        setLoading(true)
        try {
            const body = await getLosApplicationV2(loanId)
            setApplication(body.Application)
            applicationRef.current = body.Application
            const nextPartyId = pickActivePartyId(body.Application, {
                currentPartyId: activePartyIdRef.current,
                partyId,
                partyName,
                view,
                respectView: false,
            })
            setActivePartyId(nextPartyId)
            activePartyIdRef.current = nextPartyId
            if (nextPartyId && body.Application?.participantsById?.[nextPartyId]) {
                participantRef.current = body.Application.participantsById[nextPartyId]
            }
            await seedBrokerInfoFromApis()
        } catch (err) {
            message.error(err.message || "Failed to load 1003 (V2)")
        } finally {
            setLoading(false)
        }
    }, [loanId, partyId, partyName, view, seedBrokerInfoFromApis])

    useEffect(() => {
        load()
    }, [load])

    const loadCreditReports = useCallback(async () => {
        if (!loanId) return
        try {
            const data = await fetchCreditReports(loanId, 50)
            const latest = data[0] || null
            setSelectedCreditReport(latest)
            setCreditLiability(latest?.liabilities || [])
        } catch (err) {
            console.error(err)
        }
    }, [loanId])

    useEffect(() => {
        loadCreditReports()
    }, [loadCreditReports])

    const openCreditImport = useCallback((section) => {
        track(STATSIG.LOAN.IMPORT_FROM_CREDIT_REPORT, 'click_open_popup', {
            type: 'click_open_popup',
            section: section === 'section3' ? 'section3' : 'section2c',
        })
        if (changeTab) changeTab("creditReport")
    }, [changeTab])

    useEffect(() => {
        if (partyId) {
            setActivePartyId(partyId)
            activePartyIdRef.current = partyId
            return
        }
        const app = applicationRef.current
        if (!app) return
        const nextPartyId = pickActivePartyId(app, {
            currentPartyId: activePartyIdRef.current,
            partyId,
            partyName,
            view,
            respectView: true,
        })
        setActivePartyId(nextPartyId)
        activePartyIdRef.current = nextPartyId
        if (nextPartyId && app.participantsById?.[nextPartyId]) {
            participantRef.current = app.participantsById[nextPartyId]
        }
    }, [partyId, partyName, view])

    useEffect(() => {
        if (!targetPartyId) {
            appliedTargetPartyRef.current = ""
            return
        }
        if (loading || !application) return
        if (appliedTargetPartyRef.current === targetPartyId) return
        if (!application.participantsById?.[targetPartyId]) return

        appliedTargetPartyRef.current = targetPartyId
        setActivePartyId(targetPartyId)
        activePartyIdRef.current = targetPartyId
        participantRef.current = application.participantsById[targetPartyId]
        if (changeView) {
            const nextView = targetPartyId === application.primaryPartyId ? "borrower" : "coborrower"
            if (nextView !== view) changeView(nextView)
        }
        if (onTargetPartyApplied) onTargetPartyApplied()
    }, [loading, application, targetPartyId, changeView, view, onTargetPartyApplied])

    useEffect(() => {
        if (loading || !application || !targetHref) return
        if (scrolledHrefRef.current === targetHref) return
        scrolledHrefRef.current = targetHref
        const href = targetHref.startsWith("#") ? targetHref : `#${targetHref}`
        scrollToAnchor(href)
    }, [loading, application, targetHref])

    const partyParticipants = useMemo(() => listBorrowerParticipants(application), [application])

    const coborrowerCount = useMemo(
        () => partyParticipants.filter((p) => p.partyId !== application?.primaryPartyId && !p.isPrimary).length,
        [partyParticipants, application?.primaryPartyId],
    )

    // Align with backend updateBorrowerSideV2CoborrowerModeFromCountTx:
    // any co-borrower (participant count > 1) forces joint credit.
    const typeOfCreditLockedByCount = coborrowerCount >= 1

    const section1aIncomplete = useMemo(() => {
        if (!showPartySwitcher || !participant) return false
        const checks = [
            participant.residenceOwnership,
            participant.presentAddress?.months,
            participant.presentAddress?.years,
            participant.presentAddress?.street,
            participant.email,
            participant.homePhone,
            participant.maritalStatus,
            application?.hascoborrower,
            participant.firstName,
            participant.lastName,
            participant.socialSecurity,
            participant.dateOfBirth,
        ]
        if (!participant.sameMailingAddress) checks.push(participant.mailingAddress?.street)
        return checks.some((value) => value === "" || value === null || value === undefined)
    }, [showPartySwitcher, participant, application?.hascoborrower])

    const navItems = useMemo(() => {
        if (!showPartySwitcher || !section1aIncomplete) return anchorItems
        const mark = (list) => list.map((item) => ({
            ...item,
            className: item.key === "1a" ? "anchor-incomplete" : item.className,
            children: item.children ? mark(item.children) : item.children,
        }))
        return mark(anchorItems)
    }, [anchorItems, showPartySwitcher, section1aIncomplete])

    const selectParty = useCallback((nextPartyId) => {
        if (!nextPartyId) return
        setActivePartyId(nextPartyId)
        activePartyIdRef.current = nextPartyId
        const app = applicationRef.current
        if (app?.participantsById?.[nextPartyId]) {
            participantRef.current = app.participantsById[nextPartyId]
        }
        // Keep parent currentView in sync for LoanSummary deep-links (borrower vs co-borrower).
        if (!changeView || !application) return
        const isPrimary = nextPartyId === application.primaryPartyId
        const nextView = isPrimary ? "borrower" : "coborrower"
        if (nextView !== view) changeView(nextView)
    }, [application, changeView, view])

    const handleAddCoborrower = useCallback(async () => {
        if (!loanId || !editModeRef.current) return
        setAddingCoborrower(true)
        try {
            // Flush first: replace:true would otherwise drop unsaved local field edits.
            await flushPendingSavesRef.current()
            const body = await addLosCoborrowerV2(loanId)
            applyResult(body, { replace: true })
            const newPartyId = body.Participant?.partyId
            if (newPartyId) selectParty(newPartyId)
            bumpLenderFormRefresh()
            track(STATSIG.LOAN.ADD_CO_BORROWER_BUTTON, loanId, {
                loan_id: loanId,
                type: 'insert',
                borrower_count: listBorrowerParticipants(applicationRef.current).length,
            })
            message.success("Co-borrower added.")
        } catch (err) {
            message.error(err.message || "Failed to add co-borrower")
        } finally {
            setAddingCoborrower(false)
        }
    }, [loanId, selectParty, bumpLenderFormRefresh])

    const handleDeleteCoborrower = useCallback(async (deletePartyId) => {
        if (!loanId || !deletePartyId || !editModeRef.current) return
        setDeletingCoborrowerPartyId(deletePartyId)
        try {
            await flushPendingSavesRef.current()
            const body = await deleteLosCoborrowerV2(loanId, deletePartyId)
            applyResult(body, { replace: true })
            const nextApp = applicationRef.current
            if (deletePartyId === activePartyIdRef.current) {
                selectParty(nextApp?.primaryPartyId || "")
            }
            bumpLenderFormRefresh()
            track(STATSIG.LOAN.DELETE_CO_BORROWER_BUTTON, 'delete', {
                type: 'delete',
                party_id: deletePartyId,
                borrower_count: listBorrowerParticipants(applicationRef.current).length,
            })
            message.success("Co-borrower deleted.")
        } catch (err) {
            message.error(err.message || "Failed to delete co-borrower")
        } finally {
            setDeletingCoborrowerPartyId("")
        }
    }, [loanId, selectParty, bumpLenderFormRefresh])

    const notifyBorrowerAboutNewAccount = useCallback(() => {
        if (!borrowerId) return
        setHandingOffBusy(true)
        const token = sessionStorage.getItem("ZeitroA")
        fetch("/borrower/handoff", {
            method: "POST",
            body: JSON.stringify({ token: borrowerId, type: handingOffType }),
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
                Cache: "no-cache",
            },
        }).then((response) => {
            setHandingOffBusy(false)
            if (response.status !== 200) {
                message.error("Looks like there was a problem. Please try later.")
                return
            }
            message.success("An email has been sent to your borrower instructing them to log in and continue with the loan application.")
        }).catch(() => {
            setHandingOffBusy(false)
            message.error("Looks like there was a problem. Please try later.")
        })
    }, [borrowerId, handingOffType])

    // Match legacy: scroll-spy highlight inside the left 1003 pane.
    useEffect(() => {
        if (loading || !application) return undefined
        let cancelled = false
        let retryTimer

        const setup = () => {
            if (cancelled) return
            const scrollContainer = document.querySelector(".expanded.loans-left-anchor")
            if (!scrollContainer) {
                retryTimer = setTimeout(setup, 500)
                return
            }
            if (intersectionObserverRef.current) {
                intersectionObserverRef.current.disconnect()
            }
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) setActiveAnchor(entry.target.id)
                    })
                },
                {
                    root: scrollContainer,
                    rootMargin: "-20% 0px -70% 0px",
                    threshold: 0,
                },
            )
            intersectionObserverRef.current = observer
            V2_ANCHOR_IDS.forEach((id) => {
                const el = document.getElementById(id)
                if (el) observer.observe(el)
            })
            updateAnchorActiveState(activeAnchorKeyRef.current)
        }

        retryTimer = setTimeout(setup, 0)
        return () => {
            cancelled = true
            clearTimeout(retryTimer)
            if (intersectionObserverRef.current) {
                intersectionObserverRef.current.disconnect()
                intersectionObserverRef.current = null
            }
        }
    }, [loading, application, showLenderLoanForm, loanId, setActiveAnchor])

    const saveProfile = useRef(trackDebouncedSave("profile", async (current, next) => {
        const id = loanIdRef.current
        if (!id || !current?.partyId) return
        const hasCoborrower = applicationRef.current?.hascoborrower
        const body = await patchParticipantProfileV2(id, current.partyId, {
            firstName: next.firstName,
            middleName: next.middleName,
            lastName: next.lastName,
            suffix: next.suffix,
            email: next.email,
            dateOfBirth: next.dateOfBirth,
            maritalStatus: next.maritalStatus,
            hasUnmarriedRelationship: next.hasUnmarriedRelationship,
            unmarriedRelationship: next.unmarriedRelationship,
            otherUnmarriedRelationship: next.otherUnmarriedRelationship,
            stateOfUnmarriedRelationship: next.stateOfUnmarriedRelationship,
            citizenship: next.citizenship,
            visa: next.visa,
            visaExpiration: next.visaExpiration,
            homePhone: next.homePhone,
            mobilePhone: next.mobilePhone,
            workPhone: next.workPhone,
            numberOfDependents: next.numberOfDependents == null || next.numberOfDependents === ""
                ? ""
                : String(next.numberOfDependents),
            dependentsAges: next.dependentsAges || "",
            ...(hasCoborrower ? { application: { hascoborrower: hasCoborrower } } : {}),
        })
        applyResultRef.current(body)
        track(STATSIG.LOAN.UPDATE_1003, current.partyId, {
            party_id: current.partyId,
        })
        // patchLocal already updated participantRef before this save, so comparing
        // current vs next here is always equal. Use the dirty flag set in onProfile.
        if (profileNameDirtyRef.current) {
            profileNameDirtyRef.current = false
            bumpLenderFormRefreshRef.current()
        }
    }, "Failed to save profile")).current

    const saveResidence = useRef(trackDebouncedSave("residence", async (current, next) => {
        const id = loanIdRef.current
        if (!id || !current?.partyId) return
        const body = await patchParticipantResidenceV2(id, current.partyId, {
            presentAddress: next.presentAddress || emptyAddress,
            mailingAddress: next.mailingAddress || emptyAddress,
            previousAddresses: next.previousAddresses || [],
            sameMailingAddress: !!next.sameMailingAddress,
            residenceOwnership: next.residenceOwnership || "",
            monthlyRent: next.monthlyRent || "",
        })
        // Residence PATCH syncs current-residence owned property on the server.
        // Take that party's realEstate snapshot so Section 3a updates without a full reload.
        applyResultRef.current(body, {
            mergeFinanceKeys: ["realEstateByPartyId"],
            replaceActiveFinanceKeys: ["realEstateByPartyId"],
            activePartyId: current.partyId,
        })
    }, "Failed to save residence")).current

    const saveOccupationCurrent = useRef(trackDebouncedSave("occupation", async (current, occupation) => {
        const id = loanIdRef.current
        if (!id || !current?.partyId) return
        const payload = withEmployedHasOccupation(occupation, current?.otheroccupations)
        applyResultRef.current(await patchOccupationCurrentV2(id, current.partyId, payload))
    }, "Failed to save current employment")).current

    const saveEmployment = useRef(trackDebouncedSave("employment", async (current, next) => {
        const id = loanIdRef.current
        if (!id || !current?.partyId) return
        const body = await patchParticipantEmploymentV2(id, current.partyId, {
            occupation: withEmployedHasOccupation(
                next.occupation || emptyOccupation,
                next.otheroccupations
            ),
            otheroccupations: next.otheroccupations || [],
            previousoccupations: next.previousoccupations || [],
        })
        applyResultRef.current(body)
    }, "Failed to save employment")).current

    const saveSsn = useRef(trackDebouncedSave("ssn", async (current, socialSecurity) => {
        const id = loanIdRef.current
        if (!id || !current?.partyId) return
        const body = await patchParticipantSsnV2(id, current.partyId, socialSecurity)
        applyResultRef.current(body)
    }, "Failed to save SSN")).current

    const saveOtherIncome = useRef(trackDebouncedSave("other-income", async (current, next) => {
        const id = loanIdRef.current
        if (!id || !current?.partyId) return
        const body = await patchParticipantOtherIncomeV2(id, current.partyId, {
            income: next.income || {},
            serveArmedForces: next.serveArmedForces || "",
        })
        applyResultRef.current(body)
    }, "Failed to save other income")).current

    const saveAssets = useRef(trackDebouncedSave("assets", async (current, assets) => {
        const id = loanIdRef.current
        if (!id || !current?.partyId) return
        applyResultRef.current(
            await patchParticipantAssetsV2(id, current.partyId, assets),
            {
                mergeFinanceKeys: ["assetsByPartyId"],
                activePartyId: current.partyId,
            },
        )
    }, "Failed to save assets")).current

    const saveLiabilities = useRef(trackDebouncedSave("liabilities", async (current, liabilities) => {
        const id = loanIdRef.current
        if (!id || !current?.partyId) return
        applyResultRef.current(
            await patchParticipantLiabilitiesV2(id, current.partyId, liabilities),
            {
                mergeFinanceKeys: ["liabilitiesByPartyId"],
                activePartyId: current.partyId,
            },
        )
        bumpLenderFormRefreshRef.current()
    }, "Failed to save liabilities")).current

    const saveNewMortgages = useRef(trackDebouncedSave("new-mortgages", async (_target, partyId, liabilities) => {
        const id = loanIdRef.current
        if (!id || !partyId) return
        applyResultRef.current(
            await patchParticipantLiabilitiesV2(id, partyId, liabilities),
            {
                mergeFinanceKeys: ["liabilitiesByPartyId"],
                activePartyId: partyId,
            },
        )
        bumpLenderFormRefreshRef.current()
    }, "Failed to save new mortgages")).current

    const saveRealEstate = useRef(trackDebouncedSave("real-estate", async (current, realEstate) => {
        const id = loanIdRef.current
        if (!id || !current?.partyId) return
        applyResultRef.current(await patchParticipantRealEstateV2(id, current.partyId, realEstate))
    }, "Failed to save real estate")).current

    const saveSubjectProperty = useRef(trackDebouncedSave("subject-property", async (target) => {
        const app = applicationRef.current
        const id = loanIdRef.current
        if (!id || !app?.property) return
        applyResultRef.current(await patchPropertyV2(id, {
            partyId: target?.partyId || "",
            property: app.property,
            loanbalance: app.loanbalance || "",
            financingsameproperty: app.financingsameproperty,
            firsttimebuyer: app.firsttimebuyer,
            selection: app.selection || {},
        }))
        bumpLenderFormRefreshRef.current()
    }, "Failed to save property")).current

    const saveDeclarations = useRef(trackDebouncedSave("declarations", async (current, declarations, refreshLenderForm = false) => {
        const id = loanIdRef.current
        if (!id || !current?.partyId) return
        applyResultRef.current(await patchParticipantDeclarationsV2(id, current.partyId, declarations, ""))
        if (refreshLenderForm) bumpLenderFormRefreshRef.current()
    }, "Failed to save declarations")).current

    const saveBrokerInfo = useRef(trackDebouncedSave("broker-info", async (_target, brokerInfo) => {
        const id = loanIdRef.current
        if (!id) return
        applyResultRef.current(await patchBrokerInfoV2(id, brokerInfo))
    }, "Failed to save loan originator info")).current

    const savePropertyFields = useRef(trackDebouncedSave("property-fields", async () => {
        const id = loanIdRef.current
        if (!id) return
        const mapped = { ...pendingPropertyFieldsRef.current }
        pendingPropertyFieldsRef.current = {}
        if (!Object.keys(mapped).length) return
        const body = await patchPropertyFieldsV2(id, mapped)
        applyResultRef.current(body)
        if (shouldRefreshLenderForm(Object.keys(mapped))) bumpLenderFormRefreshRef.current()
    }, "Failed to save property")).current

    const saveSelectionScalars = useRef(trackDebouncedSave("selection", async () => {
        const id = loanIdRef.current
        if (!id) return
        const patch = { ...pendingSelectionRef.current }
        pendingSelectionRef.current = {}
        if (!Object.keys(patch).length) return
        const body = await patchSelectionV2(id, patch)
        applyResultRef.current(body)
        if (shouldRefreshLenderForm(Object.keys(patch))) bumpLenderFormRefreshRef.current()
    }, "Failed to save selection")).current

    const saveLoanBalance = useRef(trackDebouncedSave("loan-balance", async () => {
        const id = loanIdRef.current
        if (!id || pendingLoanBalanceRef.current === null) return
        const value = pendingLoanBalanceRef.current
        pendingLoanBalanceRef.current = null
        const body = await patchLoanFieldsV2(id, { loanbalance: value })
        applyResultRef.current(body)
    }, "Failed to save loan balance")).current

    const cancelPendingSaves = useCallback(() => {
        saveProfile.cancel()
        saveResidence.cancel()
        saveEmployment.cancel()
        saveOccupationCurrent.cancel()
        saveSsn.cancel()
        saveOtherIncome.cancel()
        saveAssets.cancel()
        saveLiabilities.cancel()
        saveNewMortgages.cancel()
        saveRealEstate.cancel()
        saveSubjectProperty.cancel()
        saveDeclarations.cancel()
        saveBrokerInfo.cancel()
        savePropertyFields.cancel()
        saveSelectionScalars.cancel()
        saveLoanBalance.cancel()
        pendingPropertyFieldsRef.current = {}
        pendingSelectionRef.current = {}
        pendingLoanBalanceRef.current = null
        pendingSaveKeysRef.current.clear()
        syncSavingIndicator()
    }, [
        saveProfile, saveResidence, saveEmployment, saveOccupationCurrent, saveSsn, saveOtherIncome,
        saveAssets, saveLiabilities, saveNewMortgages, saveRealEstate, saveSubjectProperty,
        saveDeclarations, saveBrokerInfo, savePropertyFields, saveSelectionScalars, saveLoanBalance,
    ])

    const flushPendingSaves = useCallback(async () => {
        const app = applicationRef.current
        const partyIdNow = participantRef.current?.partyId
        const assets = (app?.finance?.assetsByPartyId || {})[partyIdNow]
        const liabilities = (app?.finance?.liabilitiesByPartyId || {})[partyIdNow]
        const tasks = [
            saveProfile.flush(),
            saveResidence.flush(),
            saveEmployment.flush(),
            saveOccupationCurrent.flush(),
            saveSsn.flush(),
            saveOtherIncome.flush(),
            saveNewMortgages.flush(),
            saveRealEstate.flush(),
            saveSubjectProperty.flush(),
            saveDeclarations.flush(),
            saveBrokerInfo.flush(),
            savePropertyFields.flush(),
            saveSelectionScalars.flush(),
            saveLoanBalance.flush(),
        ]
        if (partyIdNow && assetsHaveIncompleteJointOwnership(assets, partyIdNow)) {
            saveAssets.cancel()
        } else {
            tasks.push(saveAssets.flush())
        }
        if (partyIdNow && liabilitiesHaveIncompleteJointOwnership(liabilities, partyIdNow)) {
            saveLiabilities.cancel()
        } else {
            tasks.push(saveLiabilities.flush())
        }
        await Promise.all(
            tasks
                .filter((task) => task != null)
                .map((task) => Promise.resolve(task).catch(() => {})),
        )
    }, [
        saveProfile, saveResidence, saveEmployment, saveOccupationCurrent, saveSsn, saveOtherIncome,
        saveAssets, saveLiabilities, saveNewMortgages, saveRealEstate, saveSubjectProperty,
        saveDeclarations, saveBrokerInfo, savePropertyFields, saveSelectionScalars, saveLoanBalance,
    ])
    flushPendingSavesRef.current = flushPendingSaves

    const applyEditMode = useCallback((next) => {
        // Leaving editing: flush pending field saves first, then lock UI.
        if (!next) {
            flushPendingSavesRef.current()
        }
        setEditMode(!!next)
    }, [])

    useEffect(() => {
        if (!showPartySwitcher || !application) return
        const locked = (
            application.currentStage === LS_Disposed
            || application.currentStatus === LS_LoanFunded
        ) && !checkAccess([UR_Owner, UR_LoanOfficerManager])
        if (locked) applyEditMode(false)
    }, [showPartySwitcher, application?.currentStage, application?.currentStatus, applyEditMode])

    useEffect(() => () => {
        if (!editModeRef.current) {
            cancelPendingSaves()
            return
        }
        flushPendingSaves()
    }, [cancelPendingSaves, flushPendingSaves])

    const patchLocal = (partial, extraApp) => {
        if (!editModeRef.current) return null
        const app = applicationRef.current || application
        const current = participantRef.current || participant
        if (!app || !current?.partyId) return
        const nextParticipant = { ...current, ...partial }
        const nextApp = {
            ...app,
            ...(extraApp || {}),
            participantsById: {
                ...app.participantsById,
                [current.partyId]: nextParticipant,
            },
        }
        applicationRef.current = nextApp
        participantRef.current = nextParticipant
        setApplication(nextApp)
        return nextParticipant
    }

    const onProfile = (key, value) => {
        if (key === "firstName" || key === "lastName") {
            profileNameDirtyRef.current = true
        }
        const next = patchLocal({ [key]: value })
        if (next) saveProfile(next)
    }

    const onHasCoborrower = (value) => {
        if (typeOfCreditLockedByCount) return
        const next = patchLocal({}, { hascoborrower: value })
        if (next) saveProfile(next)
        if (value === "alone") {
            const primaryId = (applicationRef.current || application)?.primaryPartyId
            if (primaryId) setActivePartyId(primaryId)
            if (onSwitchToPrimaryParty) onSwitchToPrimaryParty(primaryId)
        }
    }

    // When any co-borrower exists, keep Type of credit as joint (same as backend count rule).
    useEffect(() => {
        if (!typeOfCreditLockedByCount || !application || !editMode) return
        if (application.hascoborrower === "withcoborrower") return
        const next = patchLocal({}, { hascoborrower: "withcoborrower" })
        if (next) saveProfile(next)
    }, [typeOfCreditLockedByCount, application?.hascoborrower, editMode])

    const currentParticipant = () => participantRef.current || participant

    /** Keep Section 3a in sync with Housing / present address before residence PATCH returns. */
    const syncLocalCurrentResidenceOwned = (person) => {
        if (!editModeRef.current || !person?.partyId) return
        const app = applicationRef.current || application
        if (!app) return
        const partyId = person.partyId
        const currentKey = `current-residence:${partyId}`
        const present = person.presentAddress || emptyAddress
        const street = `${present.street || present.address || ""}`.trim()
        const isOwn = `${person.residenceOwnership || ""}`.toLowerCase() === "own"
        const existing = (app.finance?.realEstateByPartyId || {})[partyId] || { properties: [] }
        let properties = [...(existing.properties || [])].filter((item) => {
            const key = `${item?.key || ""}`
            return key !== currentKey && key !== "current-residence"
        })
        if (isOwn && street) {
            const prior = (existing.properties || []).find((item) => {
                const key = `${item?.key || ""}`
                return key === currentKey || key === "current-residence"
            }) || {}
            properties = [{
                ...prior,
                key: currentKey,
                address: present.street || present.address || "",
                street: present.street || present.address || "",
                unit: present.unit || "",
                city: present.city || "",
                county: present.county || "",
                state: present.state || "",
                zip: present.zip || "",
                zipcode: present.zip || present.zipcode || "",
                ownedFreeAndClear: prior.ownedFreeAndClear !== undefined ? prior.ownedFreeAndClear : true,
                status: prior.status || "retain",
                occupancy: prior.occupancy || "principal",
                propertyType: prior.propertyType || prior.propertytype || "singlefamily",
                marketValue: prior.marketValue || "",
                loans: Array.isArray(prior.loans) ? prior.loans : [],
                ownership: prior.ownership || "Individual",
                ownerPartyIds: Array.isArray(prior.ownerPartyIds) && prior.ownerPartyIds.length
                    ? prior.ownerPartyIds
                    : [partyId],
                homePartyId: prior.homePartyId || partyId,
            }, ...properties]
        }
        const nextApp = {
            ...app,
            finance: {
                ...(app.finance || {}),
                realEstateByPartyId: {
                    ...((app.finance || {}).realEstateByPartyId || {}),
                    [partyId]: { ...existing, properties },
                },
            },
        }
        applicationRef.current = nextApp
        setApplication(nextApp)
    }

    const onAddress = (key, value) => {
        const current = currentParticipant()
        const presentAddress = { ...(current?.presentAddress || emptyAddress), [key]: value }
        const next = patchLocal({ presentAddress })
        if (!next) return
        if (`${next.residenceOwnership || ""}`.toLowerCase() === "own") {
            syncLocalCurrentResidenceOwned(next)
        }
        saveResidence(next)
    }

    const applyPresentPlace = (parsed) => {
        const current = currentParticipant()
        const presentAddress = { ...(current?.presentAddress || emptyAddress), ...parsed }
        const next = patchLocal({ presentAddress })
        if (!next) return
        if (`${next.residenceOwnership || ""}`.toLowerCase() === "own") {
            syncLocalCurrentResidenceOwned(next)
        }
        saveResidence(next)
    }

    const applyMailingPlace = (parsed) => {
        const current = currentParticipant()
        const mailingAddress = { ...(current?.mailingAddress || emptyAddress), ...parsed }
        const next = patchLocal({ mailingAddress, sameMailingAddress: false })
        if (next) saveResidence(next)
    }

    const applyPreviousPlace = (parsed) => {
        const current = currentParticipant()
        const list = [...(current?.previousAddresses || [])]
        if (list.length === 0) list.push({ ...emptyAddress, ownership: "", rent: "" })
        list[0] = { ...list[0], ...parsed }
        onPreviousAddresses(list)
    }

    const onResidenceField = (key, value) => {
        const next = patchLocal({ [key]: value })
        if (!next) return
        if (key === "residenceOwnership") {
            syncLocalCurrentResidenceOwned(next)
        }
        saveResidence(next)
        // Housing drives Section 3a current-residence REO; flush so 3a updates without waiting for debounce.
        if (key === "residenceOwnership") {
            saveResidence.flush()
        }
    }

    const onMailingDifferent = (different) => {
        const current = currentParticipant()
        const present = current?.presentAddress || emptyAddress
        const sameMailingAddress = !different
        const mailingAddress = sameMailingAddress
            ? {
                ...(current?.mailingAddress || emptyAddress),
                street: present.street,
                city: present.city,
                county: present.county,
                state: present.state,
                zip: present.zip,
            }
            : (current?.mailingAddress || emptyAddress)
        const next = patchLocal({ sameMailingAddress, mailingAddress })
        if (next) saveResidence(next)
    }

    const onMailing = (key, value) => {
        const current = currentParticipant()
        const mailingAddress = { ...(current?.mailingAddress || emptyAddress), [key]: value }
        const next = patchLocal({ mailingAddress, sameMailingAddress: false })
        if (next) saveResidence(next)
    }

    const onPreviousAddresses = (previousAddresses) => {
        const next = patchLocal({ previousAddresses })
        if (next) saveResidence(next)
    }

    const onAddressYears = (value) => {
        const current = currentParticipant()
        const presentAddress = { ...(current?.presentAddress || emptyAddress), years: value }
        let previousAddresses = [...(current?.previousAddresses || [])]
        const years = parseInt(value, 10)
        if (value !== "" && !Number.isNaN(years) && years > 1) {
            previousAddresses = []
        } else if (value !== "" && !Number.isNaN(years) && years < 2 && previousAddresses.length === 0) {
            previousAddresses = [{ ...emptyAddress, ownership: "", rent: "" }]
        }
        const next = patchLocal({ presentAddress, previousAddresses })
        if (!next) return
        if (`${next.residenceOwnership || ""}`.toLowerCase() === "own") {
            syncLocalCurrentResidenceOwned(next)
        }
        saveResidence(next)
    }

    const setPreviousField = (key, value) => {
        const current = currentParticipant()
        const list = [...(current?.previousAddresses || [])]
        if (list.length === 0) list.push({ ...emptyAddress, ownership: "", rent: "" })
        list[0] = { ...list[0], [key]: value }
        onPreviousAddresses(list)
    }

    const openIncomeCalculation = (type, index, emp) => {
        const current = participantRef.current
        if (!editModeRef.current || !canUseIncomeCalculation(emp) || !current?.partyId) return
        track(STATSIG.LOAN.INCOME_CALCULATION, 'click_open_popup', {
            type: 'click_open_popup',
            kind: emp.selfemployed ? 'self_employed' : 'wage',
            section: type,
        })
        setIncomeCalc({
            type,
            index: index || 0,
            emp,
            partyId: current.partyId,
            personName: personDisplayNameV2(current),
        })
    }

    const applyIncomeCalculation = async (session, result) => {
        if (!loanId || !session?.partyId) return
        const sameParty = participantRef.current?.partyId === session.partyId
        try {
            if (sameParty) {
                if (session.type === "1c") {
                    await saveEmployment.flush()
                    await saveOccupationCurrent.flush()
                } else {
                    await saveOccupationCurrent.flush()
                }
            }
        } catch (err) {
            // pending field saves should not block applying calculated income
        }
        saveOccupationCurrent.cancel()
        saveEmployment.cancel()
        try {
            const body = await patchIncomeFromCalculationV2(loanId, session.partyId, {
                target: session.type,
                index: session.index || 0,
                kind: result.kind,
                from: result.startDate || "",
                income: result.kind === "wage" ? {
                    base: String(Math.round(result.amount?.base || 0)),
                    overtime: String(Math.round(result.amount?.overtime || 0)),
                    bonuses: String(Math.round(result.amount?.bonus || 0)),
                    commissions: String(Math.round(result.amount?.commission || 0)),
                    // The calculator has no military line; omit it so the 1003 amount is kept.
                    ...(result.amount?.military !== undefined ? { military: String(Math.round(result.amount.military || 0)) } : {}),
                    other: String(Math.round(result.amount?.other || 0)),
                } : undefined,
                incomeorloss: result.kind === "selfemp" ? `${result.income || ""}` : undefined,
                incomeorlossperiod: result.kind === "selfemp" ? "monthly" : undefined,
            })
            applyResult(body, { serverOccupationPartyId: session.partyId })
            track(STATSIG.LOAN.INCOME_CALCULATION, 'update_to_1003', {
                type: 'update_to_1003',
                kind: result.kind === 'selfemp' ? 'self_employed' : 'wage',
                section: session.type,
            })
        } catch (err) {
            message.error(err.message || "Failed to apply income calculation")
        }
    }

    const onOccupation = (key, value) => {
        const current = currentParticipant()
        const patch = { [key]: value }
        if (key === "incomeorloss") {
            patch.incomeorlossperiod = "monthly"
        }
        const occupation = withEmployedHasOccupation(
            { ...emptyOccupation, ...(current?.occupation || {}), ...patch },
            current?.otheroccupations
        )
        const next = patchLocal({ occupation })
        if (next) saveOccupationCurrent(occupation)
    }

    const onSelfEmployed = (checked) => {
        const current = currentParticipant()
        const occupation = withEmployedHasOccupation(
            { ...emptyOccupation, ...(current?.occupation || {}), selfemployed: checked },
            current?.otheroccupations
        )
        if (checked && !`${occupation.percentownership || ""}`.length) {
            occupation.percentownership = "24"
        }
        const next = patchLocal({ occupation })
        if (next) saveOccupationCurrent(occupation)
    }

    const onOwnershipShare = (choice) => {
        const current = currentParticipant()
        const orgVal = current?.occupation?.percentownership
        let val = ""
        if (Number(orgVal) < 25 && choice === "less") val = orgVal
        else if (Number(orgVal) > 24 && choice === "more") val = orgVal
        else val = choice === "less" ? "24" : "25"
        onOccupation("percentownership", val)
    }

    const onIncome = (key, displayValue) => {
        const current = currentParticipant()
        const occupation = { ...emptyOccupation, ...(current?.occupation || {}) }
        const income = { ...emptyOccupation.income, ...(occupation.income || {}) }
        // Persist the monthly value the user is typing (including drafts like "1200.").
        // Forcing *monthly=monthly avoids yearly parseFloat(*12) round-trips that drop trailing ".".
        income[key] = displayValue
        income[`${key}monthly`] = "monthly"
        occupation.income = income
        const occupationWithStatus = withEmployedHasOccupation(occupation, current?.otheroccupations)
        const next = patchLocal({ occupation: occupationWithStatus })
        if (next) saveOccupationCurrent(occupationWithStatus)
    }

    const onEmploymentLists = (listName, list) => {
        const next = patchLocal({ [listName]: list })
        if (next) saveEmployment(next)
    }

    const onEmploymentItem = (listName, index, partial) => {
        const current = currentParticipant()
        const list = [...(current?.[listName] || [])]
        const item = list[index] || {}
        const nextItem = { ...item, ...partial }
        if (partial.income && typeof partial.income === "object" && !Array.isArray(partial.income)) {
            nextItem.income = { ...(typeof item.income === "object" && item.income ? item.income : {}), ...partial.income }
        }
        list[index] = nextItem
        onEmploymentLists(listName, list)
    }

    const onOtherIncomeList = (list) => {
        const current = currentParticipant()
        const income = { ...(current?.income || {}), otherincome: list }
        const next = patchLocal({ income })
        if (next) saveOtherIncome(next)
    }

    const onOtherIncomeItem = (index, partial) => {
        const current = currentParticipant()
        const list = [...(current?.income?.otherincome || [])]
        list[index] = { ...(list[index] || {}), ...partial }
        onOtherIncomeList(list)
    }

    const patchFinance = (key, value) => {
        if (!editModeRef.current) return
        const app = applicationRef.current || application
        const current = participantRef.current || participant
        if (!app || !current?.partyId) return
        const nextApp = {
            ...app,
            finance: {
                ...(app.finance || {}),
                [key]: {
                    ...((app.finance || {})[key] || {}),
                    [current.partyId]: value,
                },
            },
        }
        applicationRef.current = nextApp
        setApplication(nextApp)
    }

    const onAssets = (assets) => {
        if (!editModeRef.current) return
        const current = participantRef.current || participant
        patchFinance("assetsByPartyId", assets)
        // Content validates Joint Shared-with on Next; LOS autosave must wait until Joint is complete.
        if (current?.partyId && assetsHaveIncompleteJointOwnership(assets, current.partyId)) {
            saveAssets.cancel()
            return
        }
        saveAssets(assets)
    }
    const onAssetItem = (listKey, index, partial) => {
        const app = applicationRef.current || application
        const current = participantRef.current || participant
        if (!current?.partyId) return
        const assets = {
            accounts: [],
            otherAssets: [],
            gifts: [],
            lifeInsurance: [],
            ...((app?.finance?.assetsByPartyId || {})[current.partyId] || {}),
        }
        const list = [...(assets[listKey] || [])]
        list[index] = { ...(list[index] || {}), ...partial }
        onAssets({ ...assets, [listKey]: list })
    }
    const currentLiabilities = () => {
        const app = applicationRef.current || application
        const current = participantRef.current || participant
        const raw = (app?.finance?.liabilitiesByPartyId || {})[current?.partyId] || {}
        const alimony = current?.income?.alimony || []
        const additionalExpenses = (raw.additionalExpenses && raw.additionalExpenses.length)
            ? raw.additionalExpenses
            : alimony
        return {
            loans: raw.loans || [],
            newMortgages: raw.newMortgages || [],
            ...raw,
            additionalExpenses,
        }
    }

    const onLiabilities = (partial) => {
        if (!editModeRef.current) return
        const app = applicationRef.current || application
        const current = participantRef.current || participant
        if (!app || !current?.partyId) return
        const base = currentLiabilities()
        const next = {
            ...base,
            ...partial,
            loans: Array.isArray(partial.loans) ? partial.loans : base.loans,
            additionalExpenses: Array.isArray(partial.additionalExpenses) ? partial.additionalExpenses : base.additionalExpenses,
            newMortgages: Array.isArray(partial.newMortgages) ? partial.newMortgages : base.newMortgages,
        }
        const nextParticipant = {
            ...current,
            income: { ...(current.income || {}), alimony: next.additionalExpenses },
        }
        const nextApp = {
            ...app,
            finance: {
                ...(app.finance || {}),
                liabilitiesByPartyId: {
                    ...((app.finance || {}).liabilitiesByPartyId || {}),
                    [current.partyId]: next,
                },
            },
            participantsById: {
                ...app.participantsById,
                [current.partyId]: nextParticipant,
            },
        }
        applicationRef.current = nextApp
        participantRef.current = nextParticipant
        setApplication(nextApp)
        // Content validates Joint Shared-with on Next; LOS autosave must wait until Joint is complete.
        if (liabilitiesHaveIncompleteJointOwnership(next, current.partyId)) {
            saveLiabilities.cancel()
            return
        }
        saveLiabilities(next)
    }
    const onNewMortgages = (newMortgages) => {
        if (!editModeRef.current) return
        const app = applicationRef.current || application
        const primaryId = app?.primaryPartyId
        if (!app || !primaryId) return
        const raw = (app.finance?.liabilitiesByPartyId || {})[primaryId] || {}
        const next = {
            loans: raw.loans || [],
            ...raw,
            newMortgages,
        }
        // New-mortgage edits must not rewrite 2d; liabilitiesByPartyId often has no
        // additionalExpenses while alimony lives on income.
        delete next.additionalExpenses
        const nextApp = {
            ...app,
            finance: {
                ...(app.finance || {}),
                liabilitiesByPartyId: {
                    ...((app.finance || {}).liabilitiesByPartyId || {}),
                    [primaryId]: next,
                },
            },
        }
        applicationRef.current = nextApp
        setApplication(nextApp)
        saveNewMortgages(primaryId, next)
    }
    const onLiabilityItem = (listKey, index, partial) => {
        const liabilities = currentLiabilities()
        const list = [...(liabilities[listKey] || [])]
        list[index] = { ...(list[index] || {}), ...partial }
        onLiabilities({ ...liabilities, [listKey]: list })
    }
    const onRealEstate = (realEstate) => {
        if (!editModeRef.current) return
        patchFinance("realEstateByPartyId", realEstate)
        saveRealEstate(realEstate)
    }
    const onReoItem = (index, partial) => {
        const app = applicationRef.current || application
        const current = participantRef.current || participant
        if (!current?.partyId) return
        const properties = [...((app?.finance?.realEstateByPartyId || {})[current.partyId]?.properties || [])]
        const item = properties[index] || {}
        let nextItem = { ...item, ...partial }
        if (partial.address && partial.street === undefined) nextItem.street = partial.address
        if (partial.zip) nextItem.zipcode = partial.zip
        if (partial.zipcode) nextItem.zip = partial.zipcode
        properties[index] = nextItem
        onRealEstate({ properties })
    }
    const onProperty = (key, value) => {
        if (!editModeRef.current) return
        const app = applicationRef.current || application
        if (!app) return
        const nextApp = { ...app, property: { ...(app.property || {}), [key]: value } }
        applicationRef.current = nextApp
        setApplication(nextApp)
        pendingPropertyFieldsRef.current = { ...pendingPropertyFieldsRef.current, [key]: `${value}` }
        savePropertyFields()
    }
    const onPropertyFields = (fields) => {
        if (!editModeRef.current) return
        const app = applicationRef.current || application
        if (!app) return
        const nextApp = { ...app, property: { ...(app.property || {}), ...fields } }
        applicationRef.current = nextApp
        setApplication(nextApp)
        const allowed = {
            address: true, unit: true, city: true, state: true, county: true, zipcode: true,
            purpose: true, propertytype: true, numberofunits: true, appraisal: true, salesprice: true,
            status: true, occupancy: true, hascontract: true, buyforsomeoneelse: true,
            ismixeduse: true, qualifiesforfhasecondary: true, ismanufacturedhome: true,
            insurancemaintenancetaxes: true, insurancemain: true,
            insurancetax: true, insurancehoa: true, insurancepmi: true, grossrentalincome: true, netrentalincome: true,
        }
        const mapped = {}
        Object.entries(fields || {}).forEach(([key, value]) => {
            if (key === "zip") mapped.zipcode = `${value}`
            else if (key === "propertyType") mapped.propertytype = `${value}`
            else if (key === "grossRentalIncome") mapped.grossrentalincome = `${value}`
            else if (key === "insuranceMaintenanceTaxes") mapped.insurancemaintenancetaxes = `${value}`
            else if (key === "isMixedUse") mapped.ismixeduse = `${value}`
            else if (key === "isManufacturedHome") mapped.ismanufacturedhome = `${value}`
            else if (key === "buyForSomeoneElse" || key === "buyforsomeoneelse") mapped.buyforsomeoneelse = `${value}`
            else if (allowed[key] || allowed[`${key}`.toLowerCase()]) mapped[key] = `${value}`
        })
        if (Object.keys(mapped).length) {
            pendingPropertyFieldsRef.current = { ...pendingPropertyFieldsRef.current, ...mapped }
            savePropertyFields()
        }
    }
    const onSubjectLoans = (loans) => {
        if (!editModeRef.current) return
        const app = applicationRef.current || application
        if (!app) return
        const nextApp = { ...app, property: { ...(app.property || {}), loans } }
        applicationRef.current = nextApp
        setApplication(nextApp)
        saveSubjectProperty()
    }
    const onSubjectMerge = (fields) => {
        if (!editModeRef.current) return
        const app = applicationRef.current || application
        if (!app) return
        const nextProperty = { ...(app.property || {}), ...fields }
        if (fields?.mobilehomeinfo) {
            nextProperty.mobilehomeinfo = { ...((app.property || {}).mobilehomeinfo || {}), ...fields.mobilehomeinfo }
        }
        if (fields?.purchase) {
            nextProperty.purchase = { ...((app.property || {}).purchase || {}), ...fields.purchase }
        }
        const nextApp = { ...app, property: nextProperty }
        applicationRef.current = nextApp
        setApplication(nextApp)
        saveSubjectProperty()
    }
    const onSelection = (key, value) => {
        if (!editModeRef.current) return
        const app = applicationRef.current || application
        if (!app) return
        const nextApp = { ...app, selection: { ...(app.selection || {}), [key]: value } }
        applicationRef.current = nextApp
        setApplication(nextApp)
        pendingSelectionRef.current = { ...pendingSelectionRef.current, [key]: value }
        saveSelectionScalars()
    }
    const onLoanBalance = (value) => {
        if (!editModeRef.current) return
        const app = applicationRef.current || application
        if (!app) return
        const nextApp = { ...app, loanbalance: value }
        applicationRef.current = nextApp
        setApplication(nextApp)
        pendingLoanBalanceRef.current = value
        saveLoanBalance()
    }
    const onDeclaration = (key, value) => {
        if (!editModeRef.current) return
        const app = applicationRef.current || application
        const party = participantRef.current || participant
        if (!app || !party?.partyId) return
        const partyId = party.partyId
        const current = app.declarationsByPartyId?.[partyId] || {}
        const declarations = { ...current, [key]: value }
        const nextApp = {
            ...app,
            declarationsByPartyId: {
                ...(app.declarationsByPartyId || {}),
                [partyId]: declarations,
            },
        }
        applicationRef.current = nextApp
        setApplication(nextApp)
        saveDeclarations(declarations, shouldRefreshLenderForm(key))
    }

    const onBrokerInfo = (partial) => {
        if (!editModeRef.current) return
        const app = applicationRef.current || application
        if (!app) return
        const brokerInfo = { ...(app.brokerInfo || {}), ...partial }
        const nextApp = { ...app, brokerInfo }
        applicationRef.current = nextApp
        setApplication(nextApp)
        saveBrokerInfo(brokerInfo)
    }

    const onBrokerPlace = (parsed) => {
        onBrokerInfo({
            street: parsed.street || "",
            unit: parsed.unit || "",
            city: parsed.city || "",
            county: parsed.county || "",
            state: parsed.state || "",
            zip: parsed.zip || "",
        })
    }

    if (loading && !participant) {
        return (
            <div style={{ padding: 40, textAlign: "center" }}>
                <Spin />
            </div>
        )
    }

    if (!participant) {
        return (
            <div style={{ padding: 24, color: "#545459" }}>
                No V2 participant found for this request.
            </div>
        )
    }

    return (
        <FormChromeProvider editMode={editMode} showRequiredHints={showPartySwitcher}>
        <div className="interview-wrap text-left">
            {showPartySwitcher ? (
                <div className="title1003 title1003-mobile urla-header">
                    <div className="urla-header-main">
                        <div className="urla-header-title">Uniform Residential Loan Application</div>
                        <div className="urla-header-sub">
                            {partyParticipants.length} borrower{partyParticipants.length === 1 ? "" : "s"}
                            {" · "}one loan file
                            {" · "}{partyParticipants.length} URLA form{partyParticipants.length === 1 ? "" : "s"} on export (one per borrower)
                        </div>
                        <div className="urla-party-bar">
                            {partyParticipants.map((person) => {
                                const selected = person.partyId === activePartyId
                                const isPrimary = person.isPrimary || person.partyId === application?.primaryPartyId
                                const percent = participantCompletionPercent(person, application)
                                return (
                                    <div
                                        key={person.partyId}
                                        className={`urla-party-card${selected ? " selected" : ""}`}
                                    >
                                        <button
                                            type="button"
                                            className="urla-party-card-main"
                                            onClick={() => {
                                                if (person.partyId === activePartyId) return
                                                const isTargetPrimary = person.isPrimary || person.partyId === application?.primaryPartyId
                                                track(STATSIG.LOAN.BORROWER_SWITCH, person.partyId, {
                                                    party_id: person.partyId,
                                                    role: isTargetPrimary ? 'borrower' : 'coborrower',
                                                })
                                                selectParty(person.partyId)
                                            }}
                                        >
                                            <span className="urla-party-avatar">{participantInitial(person)}</span>
                                            <span className="urla-party-meta">
                                                <span className="urla-party-name">{participantDisplayName(person)}</span>
                                                <span className="urla-party-role">
                                                    {participantRoleLabel(person, application?.primaryPartyId)}
                                                    {" · "}{percent}%
                                                </span>
                                            </span>
                                        </button>
                                        {!isPrimary && editMode ? (
                                            <Popconfirm
                                                title="Delete this co-borrower?"
                                                okText="Delete"
                                                cancelText="Cancel"
                                                onConfirm={() => handleDeleteCoborrower(person.partyId)}
                                            >
                                                <button
                                                    type="button"
                                                    className="urla-party-delete"
                                                    aria-label={`Delete ${participantDisplayName(person)}`}
                                                    disabled={deletingCoborrowerPartyId === person.partyId}
                                                >
                                                    <CloseOutlined />
                                                </button>
                                            </Popconfirm>
                                        ) : null}
                                    </div>
                                )
                            })}
                            {editMode ? (
                                <Button
                                    type="dashed"
                                    className="urla-add-coborrower"
                                    icon={<PlusOutlined />}
                                    loading={addingCoborrower}
                                    onClick={handleAddCoborrower}
                                >
                                    Add co-borrower
                                </Button>
                            ) : null}
                        </div>
                    </div>
                    <div className="editing-btn d-flex align-items-center">
                        {saving ? (
                            <div className="mr-3"><Spin size="small" className="mr-1" />Saving</div>
                        ) : null}
                        {handingOff && (handingOffType === "newaccount" || handingOffType === "newloan") ? (
                            <Button
                                loading={handingOffBusy}
                                type="primary"
                                className="mr-3"
                                onClick={notifyBorrowerAboutNewAccount}
                            >
                                Notify borrower
                            </Button>
                        ) : null}
                        <div>
                            You are{" "}
                            <Dropdown
                                menu={{
                                    items: [
                                        {
                                            key: "editing",
                                            label: (
                                                <a rel="noopener noreferrer" onClick={() => applyEditMode(true)}>
                                                    editing
                                                </a>
                                            ),
                                        },
                                        {
                                            key: "viewing",
                                            label: (
                                                <a rel="noopener noreferrer" onClick={() => applyEditMode(false)}>
                                                    viewing
                                                </a>
                                            ),
                                        },
                                    ],
                                }}
                            >
                                <a onClick={(e) => e.preventDefault()}>
                                    <Space style={{ color: "#325CEB" }}>
                                        {editMode ? "editing" : "viewing"}
                                        <DownOutlined style={{ color: "#325CEB" }} />
                                    </Space>
                                </a>
                            </Dropdown>
                        </div>
                    </div>
                </div>
            ) : null}
            <Row style={{ height: "100%" }}>
                <Col
                    className={`expanded loans-left-anchor pl-0 ${showPartySwitcher ? "col-9" : "document-left-anchor"}`}
                >
            <div id="s1" className="p-4">
                <div className="fontWeight500" style={{ fontSize: 20 }}>Section 1: Borrower Information</div>
            </div>
            <div id="1a">
                <div className="form1003Title">1a. Personal Information    {showPartySwitcher && section1aIncomplete ? <span className="leted">INCOMPLETE</span> : null}</div>
                <div className="p-4">
                    <Row className="mb-4">
                        <Col sm={4}>
                            <TextField label="First name" value={participant.firstName} onChange={(v) => onProfile("firstName", v)} />
                        </Col>
                        <Col sm={4}>
                            <TextField label="Middle name" value={participant.middleName} onChange={(v) => onProfile("middleName", v)} optional />
                        </Col>
                        <Col sm={4}>
                            <TextField label="Last name" value={participant.lastName} onChange={(v) => onProfile("lastName", v)} />
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={4} className="mb-4">
                            <SelectField
                                label="Suffix"
                                value={participant.suffix || ""}
                                options={suffixOptions}
                                onChange={(v) => onProfile("suffix", v)}
                                optional
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={6} className="mb-3">
                            <TextField
                                label="Social security number"
                                value={formatSSN(participant.socialSecurity)}
                                onChange={(v) => {
                                    const ssn = formatSSN(v)
                                    patchLocal({ socialSecurity: ssn })
                                    saveSsn(ssn)
                                }}
                            />
                        </Col>
                        <Col sm={6} className="mb-3">
                            <DateField
                                label="Date of birth"
                                value={participant.dateOfBirth}
                                onChange={(v) => onProfile("dateOfBirth", v)}
                            />
                        </Col>
                    </Row>
                    <Row className="">
                        <Col sm={6}>
                            <SelectField
                                label="Citizenship"
                                value={participant.citizenship || ""}
                                options={[
                                    { value: "", label: "..." },
                                    { value: "citizen", label: "U.S. Citizen" },
                                    { value: "permanent", label: "Permanent Resident Alien" },
                                    { value: "alien", label: "Non-Permanent Resident Alien" },
                                ]}
                                onChange={(v) => onProfile("citizenship", v)}
                            />
                        </Col>
                    </Row>
                    <hr className="form1003Divider" />
                    <Row className="mb-4">
                        <Col>
                            <SelectionField
                                label="Type of credit"
                                value={typeOfCreditLockedByCount ? "withcoborrower" : (application?.hascoborrower || "alone")}
                                onChange={(v) => onHasCoborrower(v)}
                                disabled={typeOfCreditLockedByCount}
                                options={[
                                    { value: "alone", label: "I am applying for individual credit" },
                                    { value: "withcoborrower", label: "I am applying for joint credit" },
                                ]}
                            />
                                {typeOfCreditLockedByCount ? (
                                    <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                                        Locked to joint credit while this loan has co-borrowers. Remove co-borrowers to change.
                                    </div>
                                ) : null}
                        </Col>
                    </Row>
                    <hr className="form1003Divider" />
                    <Row className="mb-4">
                        <Col>
                            <SelectionField
                                label="Marital status"
                                value={participant.maritalStatus || ""}
                                onChange={(v) => onProfile("maritalStatus", v)}
                                options={[
                                    { value: "married", label: "Married" },
                                    { value: "separated", label: "Separated" },
                                    { value: "unmarried", label: "Unmarried" },
                                ]}
                            />
                        </Col>
                    </Row>
                    {participant.maritalStatus === "unmarried" ? (
                        <>
                            <Row className="mb-4">
                                <Col>
                                    <YesNoField
                                        label="If you select unmarried, is there a person who is not your legal spouse but who currently has real property rights similar to those of a legal spouse?"
                                        value={participant.hasUnmarriedRelationship || ""}
                                        onChange={(v) => onProfile("hasUnmarriedRelationship", v)}
                                    />
                                </Col>
                            </Row>
                            {participant.hasUnmarriedRelationship === "yes" ? (
                                <Row className="mb-4">
                                    <Col>
                                        <SelectionField
                                            label="If you select yes, please indicate the type of relationship and the state in which it was formed"
                                            value={participant.unmarriedRelationship || ""}
                                            onChange={(v) => onProfile("unmarriedRelationship", v)}
                                            direction="vertical"
                                            options={[
                                                { value: "civilunion", label: "Civil union" },
                                                { value: "domesticpartnership", label: "Domestic partnership" },
                                                { value: "reciprocalrelationship", label: "Registered reciprocal beneficiary relationship" },
                                                { value: "other", label: "Other" },
                                            ]}
                                        />
                                    </Col>
                                </Row>
                            ) : null}
                            {participant.unmarriedRelationship === "other" ? (
                                <Row className="mb-4">
                                    <Col>
                                        <TextField label="If you select other, please explain the type of relationship" value={participant.otherUnmarriedRelationship} onChange={(v) => onProfile("otherUnmarriedRelationship", v)} />
                                    </Col>
                                </Row>
                            ) : null}
                            {participant.hasUnmarriedRelationship === "yes" ? (
                                <Row className="mb-4">
                                    <Col>
                                        <TextField label="State (abbreviation) in which the relationship was formed" value={participant.stateOfUnmarriedRelationship} onChange={(v) => onProfile("stateOfUnmarriedRelationship", v)} />
                                    </Col>
                                </Row>
                            ) : null}
                        </>
                    ) : null}
                    <Row className="mb-4">
                        <Col sm={6} md={12}>
                            <PrefixField
                                label="Dependents (not listed by another borrower)"
                                prefix="Number"
                                value={participant.numberOfDependents}
                                onChange={(v) => {
                                    const raw = `${v}`.replace(/\D/g, "")
                                    if (raw === "") {
                                        const next = patchLocal({
                                            numberOfDependents: "",
                                            dependentsAges: "",
                                        })
                                        if (next) saveProfile(next)
                                        return
                                    }
                                    const n = Math.min(20, Math.max(0, parseInt(raw, 10) || 0))
                                    const ages = `${participant.dependentsAges || ""}`.split(",")
                                    const nextAges = Array.from({ length: n }, (_, i) => ages[i] || "")
                                    const next = patchLocal({
                                        numberOfDependents: String(n),
                                        dependentsAges: n > 0 ? nextAges.join(",") : "",
                                    })
                                    if (next) saveProfile(next)
                                }}
                            />
                        </Col>
                    </Row>
                    {parseInt(participant.numberOfDependents, 10) > 0 ? (
                        <Row>
                            {Array.from({ length: Math.min(20, parseInt(participant.numberOfDependents, 10) || 0) }).map((_, i) => {
                                const ages = `${participant.dependentsAges || ""}`.split(",")
                                return (
                                    <Col sm={6} className="mb-4" key={i}>
                                        <PrefixField
                                            label={`Age of dependent ${i + 1}`}
                                            prefix="Age"
                                            value={ages[i] || ""}
                                            onChange={(v) => {
                                                const nextAges = `${participant.dependentsAges || ""}`.split(",")
                                                while (nextAges.length <= i) nextAges.push("")
                                                nextAges[i] = v
                                                onProfile("dependentsAges", nextAges.join(","))
                                            }}
                                        />
                                    </Col>
                                )
                            })}
                        </Row>
                    ) : null}
                    <hr className="form1003Divider" />
                    <Row className="mb-3">
                        <Col sm={6}>
                            <PrefixField
                                label="Contact"
                                prefix="Email"
                                value={participant.email}
                                onChange={(v) => onProfile("email", v)}
                                error={emailFormatError(participant.email)}
                            />
                        </Col>
                    </Row>
                    <Row className="mb-4 form1003-phone-row">
                        <Col sm={4}>
                            <PrefixField prefix="Home phone" value={participant.homePhone} onChange={(v) => onProfile("homePhone", com.fixPhoneInput(v))} />
                        </Col>
                        <Col sm={4}>
                            <PrefixField prefix="Mobile phone" value={participant.mobilePhone} onChange={(v) => onProfile("mobilePhone", com.fixPhoneInput(v))} />
                        </Col>
                        <Col sm={4}>
                            <PrefixField prefix="Work phone" value={participant.workPhone} onChange={(v) => onProfile("workPhone", com.fixPhoneInput(v))} />
                        </Col>
                    </Row>
                    <hr className="form1003Divider" />
                    <StructuredAddressField
                        id={`v2-present-address-${participant.partyId}`}
                        label="Current residential address"
                        address={participant.presentAddress}
                        onChange={onAddress}
                        onPlace={applyPresentPlace}
                        loanLimits={loanLimits}
                    />
                    {showNonOwnSharedOwnerWarning ? (
                        <NonOwnSharedOwnerWarningBanner
                            message={nonOwnSharedOwnerWarningText(participantDisplayName(participant), "section")}
                            onDismiss={onDismissNonOwnSharedOwnerWarning}
                        />
                    ) : null}
                    <Row className="mb-4">
                        <Col>
                            <Field label="How long have you been at your current address?">
                                <DurationFields
                                    years={participant.presentAddress?.years}
                                    months={participant.presentAddress?.months}
                                    onYearsChange={onAddressYears}
                                    onMonthsChange={(v) => onAddress("months", v)}
                                />
                            </Field>
                        </Col>
                    </Row>
                    <Row className="mb-4">
                        <Col>
                            <SelectionField
                                label="Housing"
                                value={participant.residenceOwnership || ""}
                                onChange={(v) => onResidenceField("residenceOwnership", v)}
                                options={[
                                    { value: "rentfree", label: "No primary housing expense" },
                                    { value: "own", label: "Own" },
                                    { value: "rent", label: "Rent" },
                                ]}
                            />
                        </Col>
                    </Row>
                    {participant.residenceOwnership === "rent" ? (
                        <Row className="mb-4">
                            <Col sm={6}>
                                <PrefixField label="Monthly Rent" prefix="$" value={participant.monthlyRent} onChange={(v) => onResidenceField("monthlyRent", moneyTypingValue(v, 2, true))} />
                            </Col>
                        </Row>
                    ) : null}
                    {parseInt(participant.presentAddress?.years, 10) < 2 ? (
                        <>
                            <hr className="form1003Divider" />
                            <StructuredAddressField
                                id={`v2-previous-address-${participant.partyId}`}
                                label="Previous residential address"
                                address={(participant.previousAddresses || [])[0]}
                                onChange={setPreviousField}
                                onPlace={applyPreviousPlace}
                                loanLimits={loanLimits}
                            />
                            <Row className="mb-4">
                                <Col>
                                    <Field label="How long have you been at your previous address?">
                                        <DurationFields
                                            years={(participant.previousAddresses || [])[0]?.years}
                                            months={(participant.previousAddresses || [])[0]?.months}
                                            onYearsChange={(v) => setPreviousField("years", v)}
                                            onMonthsChange={(v) => setPreviousField("months", v)}
                                        />
                                    </Field>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col>
                                    <SelectionField
                                        label="Housing"
                                        value={(participant.previousAddresses || [])[0]?.ownership || ""}
                                        onChange={(v) => setPreviousField("ownership", v)}
                                        options={[
                                            { value: "rentfree", label: "No primary housing expense" },
                                            { value: "own", label: "Own" },
                                            { value: "rent", label: "Rent" },
                                        ]}
                                    />
                                </Col>
                            </Row>
                            {(participant.previousAddresses || [])[0]?.ownership === "rent" ? (
                                <Row className="mb-4">
                                    <Col md={6}>
                                        <PrefixField label="Monthly Rent" prefix="$" value={(participant.previousAddresses || [])[0]?.rent} onChange={(v) => setPreviousField("rent", moneyTypingValue(v, 2, true))} />
                                    </Col>
                                </Row>
                            ) : null}
                        </>
                    ) : null}
                    <Row className="mb-4">
                        <Col>
                            <ChromeCheckbox
                                checked={!participant.sameMailingAddress}
                                onChange={(e) => onMailingDifferent(e.target.checked)}
                            >
                                Mailing address is different from current address
                            </ChromeCheckbox>
                        </Col>
                    </Row>
                    {!participant.sameMailingAddress ? (
                        <>
                            <StructuredAddressField
                                id={`v2-mailing-address-${participant.partyId}`}
                                label="Mailing Address"
                                address={participant.mailingAddress}
                                onChange={onMailing}
                                onPlace={applyMailingPlace}
                                loanLimits={loanLimits}
                            />
                        </>
                    ) : null}
                </div>
            </div>
            <div id="1b">
                <div className="form1003Title">1b. Employment and Income
                    {editMode ? (
                    canUseIncomeCalculation(participant.occupation) ? (
                        <span
                            className="income-calculation-btn"
                            style={{ cursor: "pointer" }}
                            onClick={() => openIncomeCalculation("1b", 0, participant.occupation)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <g clipPath="url(#clip0_v2_1b)">
                                    <path d="M2.64435 1.3999V3.88879M1.3999 2.64435H3.88879M3.26657 10.111V12.5999M2.02212 11.3555H4.51101M7.62213 1.3999L9.04435 5.66657L12.5999 6.9999L9.04435 8.33324L7.62213 12.5999L6.1999 8.33324L2.64435 6.9999L6.1999 5.66657L7.62213 1.3999Z" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                </g>
                                <defs>
                                    <clipPath id="clip0_v2_1b">
                                        <rect width="14" height="14" fill="white"/>
                                    </clipPath>
                                </defs>
                            </svg>
                            Income calculation
                        </span>
                    ) : (
                        <span disabled className="income-calculation-btn-disabled">Income calculation</span>
                    )
                    ) : null}
                </div>
                <div className="p-4">
                    <Row>
                        <Col sm={6} className="mb-4">
                            <TextField
                                label="Employer or business name"
                                value={participant.occupation?.employername}
                                onChange={(v) => onOccupation("employername", v)}
                            />
                        </Col>
                        <Col sm={6} className="mb-4">
                            <TextField
                                label="Phone"
                                value={com.fixPhoneInput(participant.occupation?.phone)}
                                onChange={(v) => onOccupation("phone", com.fixPhoneInput(v))}
                            />
                        </Col>
                    </Row>
                    <StructuredAddressField
                        id={`v2-employer-address-${participant.partyId}`}
                        label="Address"
                        address={{
                            street: participant.occupation?.employerstreet || participant.occupation?.employeraddress,
                            unit: participant.occupation?.employerunit,
                            state: participant.occupation?.employerstate,
                            county: participant.occupation?.employercounty,
                            city: participant.occupation?.employercity,
                            zip: participant.occupation?.employerzip,
                        }}
                        onChange={(field, value) => onOccupation(`employer${field === "street" ? "street" : field}`, value)}
                        onPlace={(parsed) => {
                            onOccupation("employerstreet", parsed.street)
                            onOccupation("employercity", parsed.city)
                            onOccupation("employerstate", parsed.state)
                            onOccupation("employercounty", parsed.county)
                            onOccupation("employerzip", parsed.zip)
                        }}
                        loanLimits={loanLimits}
                    />
                    <Row className="mb-4">
                        <Col sm={6} className="mb-4">
                            <TextField
                                label="Position or title"
                                value={participant.occupation?.positiontitle}
                                onChange={(v) => onOccupation("positiontitle", v)}
                            />
                        </Col>
                        <Col sm={6} className="mb-4">
                            <DateField
                                label="Start date"
                                value={participant.occupation?.from}
                                onChange={(v) => onOccupation("from", v)}
                            />
                        </Col>
                    </Row>
                    <Row className="mb-4">
                        <Col>
                            <Field label="How long in this line of work?">
                                <DurationFields
                                    years={getWorkDuration(participant.occupation).years}
                                    months={getWorkDuration(participant.occupation).months}
                                    onYearsChange={(v) => onOccupation("yearsatlineofwork", v)}
                                    onMonthsChange={(v) => onOccupation("monthsatlineofwork", v)}
                                />
                            </Field>
                        </Col>
                    </Row>
                    <Row className="mb-4">
                        <Col>
                            <ChromeCheckbox className="fontWeight500" checked={!!participant.occupation?.employedbyagent} onChange={(e) => onOccupation("employedbyagent", e.target.checked)}>
                                You are employed by a family member, property seller, real estate agent, or other party to the transaction.
                            </ChromeCheckbox>
                        </Col>
                    </Row>
                    <Row className="mb-4">
                        <Col>
                            <ChromeCheckbox className="fontWeight500" checked={!!participant.occupation?.selfemployed} onChange={(e) => onSelfEmployed(e.target.checked)}>
                                Check if you are the business owner or self-employed
                            </ChromeCheckbox>
                        </Col>
                    </Row>
                    {participant.occupation?.selfemployed ? (
                        <>
                            <Row>
                                <Col sm={12} className="mb-4">
                                    <Field label="I have an ownership share of">
                                        <ChromeRadioGroup
                                            value={ownershipShareValue(participant.occupation?.percentownership)}
                                            onChange={(e) => onOwnershipShare(e.target.value)}
                                            options={[
                                                { value: "less", label: "less than 25%" },
                                                { value: "more", label: "25% or more" },
                                            ]}
                                        >
                                            <div style={{ display: "flex", gap: 10, width: "100%" }}>
                                                <div style={{ border: "1px solid #ececec", borderRadius: 4, padding: "0 10px", backgroundColor: "#fcfcfc", height: 44, display: "flex", alignItems: "center", justifyContent: "left", flex: 1 }}>
                                                    <AntRadio value="less">less than 25%</AntRadio>
                                                </div>
                                                <div style={{ border: "1px solid #ececec", borderRadius: 4, padding: "0 10px", backgroundColor: "#fcfcfc", height: 44, display: "flex", alignItems: "center", justifyContent: "left", flex: 1 }}>
                                                    <AntRadio value="more">25% or more</AntRadio>
                                                </div>
                                            </div>
                                        </ChromeRadioGroup>
                                    </Field>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm={6} className="mb-4">
                                    <MoneyTextField label="Monthly Income (or Loss)" value={displayMonthlyIncomeOrLoss(participant.occupation)} onChange={(v) => onOccupation("incomeorloss", v)} />
                                </Col>
                            </Row>
                        </>
                    ) : (
                        <>
                            <hr className="form1003Divider" />
                            <Row>
                                <Col className="mb-4">
                                    <Field label="Gross monthly income">
                                        <Row>
                                            <Col sm={6} className="mb-3">
                                                <MoneyPrefixField prefix="Base" suffix="/ month" value={displayMonthlyIncome(participant.occupation?.income, "base")} onChange={(v) => onIncome("base", v)} />
                                            </Col>
                                            <Col sm={6} className="mb-3">
                                                <MoneyPrefixField prefix="Overtime" suffix="/ month" value={displayMonthlyIncome(participant.occupation?.income, "overtime")} onChange={(v) => onIncome("overtime", v)} />
                                            </Col>
                                        </Row>
                                    </Field>
                                </Col>
                            </Row>
                            <Row>
                                <Col className="mb-4">
                                    <Row>
                                        <Col sm={6} className="mb-3">
                                            <MoneyPrefixField prefix="Bonus" suffix="/ month" value={displayMonthlyIncome(participant.occupation?.income, "bonuses")} onChange={(v) => onIncome("bonuses", v)} />
                                        </Col>
                                        <Col sm={6} className="mb-3">
                                            <MoneyPrefixField prefix="Commission" suffix="/ month" value={displayMonthlyIncome(participant.occupation?.income, "commissions")} onChange={(v) => onIncome("commissions", v)} />
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                            <Row>
                                <Col className="mb-4">
                                    <Row>
                                        <Col sm={6} className="mb-3">
                                            <MoneyPrefixField prefix="Military" suffix="/ month" value={displayMonthlyIncome(participant.occupation?.income, "military")} onChange={(v) => onIncome("military", v)} />
                                        </Col>
                                        <Col sm={6} className="mb-3">
                                            <MoneyPrefixField prefix="Other" suffix="/ month" value={displayMonthlyIncome(participant.occupation?.income, "other")} onChange={(v) => onIncome("other", v)} />
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col sm={6}>
                                    <PrefixField
                                        label="Total income"
                                        prefix="$"
                                        suffix="/ month"
                                        disabled
                                        value={sumMonthlyIncomeFields(participant.occupation?.income)}
                                    />
                                </Col>
                            </Row>
                        </>
                    )}
                </div>
            </div>
            <Interview1003ViewV2More
                participant={participant}
                application={application}
                showNonOwnSharedOwnerWarning={showNonOwnSharedOwnerWarning}
                onDismissNonOwnSharedOwnerWarning={onDismissNonOwnSharedOwnerWarning}
                onOpenCreditImport={openCreditImport}
                hasAvailableCreditReport={creditLiability.length > 0}
                creditReportCreatedAt={selectedCreditReport?.createdAt || ""}
                onOpenIncomeCalculation={openIncomeCalculation}
                onEmploymentLists={onEmploymentLists}
                onEmploymentItem={onEmploymentItem}
                onOtherIncome={onOtherIncomeList}
                onOtherIncomeItem={onOtherIncomeItem}
                onAssets={onAssets}
                onAssetItem={onAssetItem}
                onLiabilities={onLiabilities}
                onLiabilityItem={onLiabilityItem}
                onNewMortgages={onNewMortgages}
                onRealEstate={onRealEstate}
                onReoItem={onReoItem}
                onSubjectLoans={onSubjectLoans}
                onProperty={onProperty}
                onPropertyFields={onPropertyFields}
                onSubjectMerge={onSubjectMerge}
                onSelection={onSelection}
                onLoanBalance={onLoanBalance}
                onDeclaration={onDeclaration}
                onBrokerInfo={onBrokerInfo}
                onBrokerPlace={onBrokerPlace}
                loanLimits={loanLimits}
            />
            {showLenderLoanForm ? (
                <LenderLoanFormV2
                    loanId={loanId}
                    editMode={editMode}
                    refreshToken={lenderFormRefreshToken}
                />
            ) : null}
            {incomeCalc ? (
                incomeCalc.emp?.selfemployed ? (
                    <BusinessIncomeCalculationModalV2
                        open
                        loanId={loanId}
                        currentView="borrower"
                        incomeType={incomeCalc.type}
                        currentEmpData={incomeCalc.emp}
                        businessName={incomeCalc.emp?.employername}
                        personName={incomeCalc.personName}
                        application={adaptV2ApplicationForIncomeCalc(application, incomeCalc.partyId)}
                        borrower={adaptV2ApplicationForIncomeCalc(application, incomeCalc.partyId).borrower}
                        onCancel={() => setIncomeCalc(null)}
                        onApply={(result) => {
                            applyIncomeCalculation(incomeCalc, result)
                            setIncomeCalc(null)
                        }}
                    />
                ) : (
                    <IncomeCalculationModalV2
                        open
                        loanId={loanId}
                        currentView="borrower"
                        incomeType={incomeCalc.type}
                        currentEmpData={incomeCalc.emp}
                        personName={incomeCalc.personName}
                        application={adaptV2ApplicationForIncomeCalc(application, incomeCalc.partyId)}
                        onCancel={() => setIncomeCalc(null)}
                        onApply={(result) => {
                            applyIncomeCalculation(incomeCalc, result)
                            setIncomeCalc(null)
                        }}
                    />
                )
            ) : null}
                </Col>
                <Col className={`expanded loans-rigth-anchor ${showPartySwitcher ? "col-3" : "document-rigth-anchor pl-0 pr-0"}`}>
                    {showPartySwitcher ? (
                        <div className="d-flex">
                            <div style={{ color: "#6E6E70", marginLeft: 10, marginTop: 36 }}>
                                QUICK GUIDE
                            </div>
                        </div>
                    ) : null}
                    <Anchor
                        affix={false}
                        items={navItems}
                        getCurrentAnchor={() => `#${activeAnchorKey}`}
                        onChange={(currentActiveLink) => {
                            const anchorId = `${currentActiveLink || ""}`.replace("#", "")
                            if (anchorId) setActiveAnchor(anchorId)
                        }}
                        onClick={(e, link) => {
                            e.preventDefault()
                            const anchorId = `${link?.href || ""}`.replace("#", "")
                            if (!anchorId) return
                            scrollToAnchor(`#${anchorId}`)
                            setActiveAnchor(anchorId)
                        }}
                    />
                </Col>
            </Row>
        </div>
        </FormChromeProvider>
    )
}
